/**
 * SessionMessage component for displaying chat messages
 * Renders user, assistant, system, and tool messages with different styling
 * Phase 9.3: Added AgentIndicator and role-specific styling
 * Phase 9.4: Added collapsible long content and text wrapping
 */
import { useState, useMemo } from 'react'
import type { SessionMessage as SessionMessageType } from '@/lib/types'
import { cn } from '@/lib/utils'
import { User, Settings, Wrench, ChevronDown, ChevronRight } from 'lucide-react'

/**
 * Ignix67 Logo Icon - Flame/spark mark
 * Used for assistant messages to reinforce branding
 */
function Ignix67Icon({ className }: { className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 64 64" 
      fill="none" 
      className={className}
      aria-label="Ignix67 assistant"
    >
      {/* Background */}
      <rect width="64" height="64" rx="14" fill="currentColor" opacity="0.15"/>
      {/* Flame mark */}
      <path d="M32 12c0 0-12 14-12 24a12 12 0 0 0 24 0c0-10-12-24-12-24z" fill="currentColor" opacity="0.9"/>
      <path d="M32 22c0 0-6 8-6 14a6 6 0 0 0 12 0c0-6-6-14-6-14z" fill="hsl(258, 58%, 54%)" />
    </svg>
  )
}
import { AgentIndicator } from './AgentIndicator'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button'
import { MarkdownContent } from '@/components/common/MarkdownContent'
import { StructuredContentRenderer } from '@/components/chatbot/StructuredContentRenderer'

// Threshold for collapsible content
const COLLAPSE_THRESHOLD_CHARS = 300
const COLLAPSE_PREVIEW_LINES = 4

/**
 * Get preview of content for collapsed state
 * For JSON content, shows a summary label
 * @param extractedJson - Pre-computed JSON extraction to avoid re-parsing
 */
function getPreview(content: string, role?: string, extractedJson?: { json: string; before: string; after: string } | null): string {
  // For tool JSON content, return a brief indicator
  if (role === 'tool' && extractedJson) {
    try {
      const parsed = JSON.parse(extractedJson.json)
      // Try to show the action type if available
      if (parsed.action) {
        return `Tool response: ${parsed.action}`
      }
      if (Array.isArray(parsed)) {
        return `Tool response: ${parsed.length} items`
      }
      return 'Tool response (click to expand)'
    } catch {
      return 'Tool response (click to expand)'
    }
  }
  
  const lines = content.split('\n')
  if (lines.length > COLLAPSE_PREVIEW_LINES) {
    return lines.slice(0, COLLAPSE_PREVIEW_LINES).join('\n') + '\n...'
  }
  if (content.length > COLLAPSE_THRESHOLD_CHARS) {
    return content.slice(0, COLLAPSE_THRESHOLD_CHARS) + '...'
  }
  return content
}

export interface SessionMessageProps {
  /** The message to display */
  message: SessionMessageType
  /** Whether to show the timestamp */
  showTimestamp?: boolean
  /** Whether to show the role label (You/Assistant) */
  showRoleLabel?: boolean
  /** Display variant */
  variant?: 'default' | 'compact'
  /**
   * Force the full content to render with no length-based collapse. Used by the chatbot turn view, where an
   * assistant answer is always shown in full (chatbot-thinking-ux). Default `false` preserves the existing
   * length-collapse behavior for the orchestration session view and every other consumer.
   */
  disableCollapse?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Format timestamp for display
 */
function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Extract JSON from content - handles raw JSON, markdown code fences, and JSON embedded in text
 * 
 * IMPORTANT: This function must be fast - it's called multiple times per message.
 * Avoid expensive regex operations with [\s\S]* patterns that can cause backtracking.
 */
function extractJson(content: string): { json: string; before: string; after: string } | null {
  const trimmed = content.trim()
  
  // Fast path: Direct JSON object or array (entire content is JSON)
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return { json: trimmed, before: '', after: '' }
  }
  
  // Fast path: JSON wrapped in markdown code fence: ```json ... ``` or ``` ... ```
  if (trimmed.startsWith('```')) {
    const endIdx = trimmed.lastIndexOf('```')
    if (endIdx > 3) {
      // Find the end of the opening fence (first newline after ```)
      const firstNewline = trimmed.indexOf('\n')
      if (firstNewline > 0 && firstNewline < endIdx) {
        const inner = trimmed.slice(firstNewline + 1, endIdx).trim()
        if ((inner.startsWith('{') && inner.endsWith('}')) ||
            (inner.startsWith('[') && inner.endsWith(']'))) {
          return { json: inner, before: '', after: '' }
        }
      }
    }
  }
  
  // Look for JSON object with "action" field embedded in text
  // Use indexOf for fast detection before attempting parse
  const actionIdx = trimmed.indexOf('"action"')
  if (actionIdx === -1) {
    return null
  }
  
  // Find the opening brace before "action"
  const braceIdx = trimmed.lastIndexOf('{', actionIdx)
  if (braceIdx === -1) {
    return null
  }
  
  // Try to find matching closing brace by counting braces
  let depth = 0
  let endIdx = -1
  for (let i = braceIdx; i < trimmed.length; i++) {
    const char = trimmed[i]
    if (char === '{') depth++
    else if (char === '}') {
      depth--
      if (depth === 0) {
        endIdx = i
        break
      }
    }
  }
  
  if (endIdx === -1) {
    return null
  }
  
  const potentialJson = trimmed.slice(braceIdx, endIdx + 1)
  
  // Validate it's parseable JSON
  try {
    JSON.parse(potentialJson)
    return {
      json: potentialJson,
      before: trimmed.slice(0, braceIdx).trim(),
      after: trimmed.slice(endIdx + 1).trim()
    }
  } catch {
    return null
  }
}

/**
 * Render message content - uses StructuredContentRenderer for JSON-like content
 * in tool/assistant messages, MarkdownContent otherwise
 */
function renderContent(content: string, role: string): React.ReactNode {
  const isUser = role === 'user'
  
  // For tool and assistant messages, check if content contains JSON
  // and use StructuredContentRenderer for nice card rendering
  if (role === 'tool' || role === 'assistant') {
    const extracted = extractJson(content)
    if (extracted) {
      const { json, before, after } = extracted
      // If there's text before/after the JSON, render both
      if (before || after) {
        return (
          <>
            {before && <MarkdownContent content={before} variant="default" />}
            <StructuredContentRenderer content={json} />
            {after && <MarkdownContent content={after} variant="default" />}
          </>
        )
      }
      return <StructuredContentRenderer content={json} />
    }
  }
  // For user messages use inverted colors (white on primary), others use default
  return <MarkdownContent content={content} variant="default" inverted={isUser} />
}

/**
 * Chat message bubble component.
 * 
 * Features:
 * - Different styling for user vs assistant messages
 * - User messages aligned right, assistant left
 * - Optional timestamp display
 * - Optional role label
 * - Basic markdown rendering
 * 
 * @example
 * ```tsx
 * <SessionMessage 
 *   message={{ role: 'user', content: 'Hello', timestamp: '...' }}
 *   showTimestamp={true}
 * />
 * ```
 */
export function SessionMessage({
  message,
  showTimestamp = true,
  showRoleLabel = false,
  variant = 'default',
  disableCollapse = false,
  className
}: SessionMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'
  const isTool = message.role === 'tool'
  const isCompact = variant === 'compact'
  
  // Memoize JSON extraction - this is called once per render instead of multiple times
  const extractedJson = useMemo(() => {
    if (message.role === 'tool' || message.role === 'assistant') {
      return extractJson(message.content)
    }
    return null
  }, [message.content, message.role])
  
  // Memoize collapsible state based on extracted JSON
  const isCollapsible = useMemo(() => {
    // Caller opted out of length-collapse (e.g. chatbot answers are always shown in full).
    if (disableCollapse) {
      return false
    }
    // Tool messages with JSON are always collapsible (collapsed by default)
    if (message.role === 'tool' && extractedJson) {
      return true
    }
    // Assistant JSON content renders as nice cards - don't collapse
    if (message.role === 'assistant' && extractedJson) {
      return false
    }
    return message.content.length > COLLAPSE_THRESHOLD_CHARS || message.content.split('\n').length > 6
  }, [message.content, message.role, extractedJson, disableCollapse])

  // Get the appropriate icon for the message role
  // Assistant uses Ignix67 branding icon, others use lucide icons
  const Icon = isUser ? User : isSystem ? Settings : isTool ? Wrench : Ignix67Icon

  // Get role label for display (don't show agentName here if we'll show it in badge)
  const getRoleLabel = () => {
    switch (message.role) {
      case 'user': return 'You'
      case 'system': return 'System'
      case 'tool': return 'Tool'
      default: return 'Assistant'
    }
  }

  return (
    <article
      role="article"
      aria-label={`${getRoleLabel()} message`}
      data-role={message.role}
      data-variant={variant}
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row',
        // System messages get italic styling
        isSystem && 'italic text-muted-foreground',
        // Tool messages get monospace styling
        isTool && 'font-mono',
        className
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 rounded-full flex items-center justify-center',
          isCompact ? 'w-6 h-6' : 'w-8 h-8',
          isUser 
            ? 'bg-primary text-primary-foreground' 
            : isSystem
              ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              : isTool
                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                : 'bg-muted text-muted-foreground'
        )}
      >
        <Icon className={cn(isCompact ? 'w-3 h-3' : 'w-4 h-4')} />
      </div>

      {/* Message content */}
      <div
        className={cn(
          'flex flex-col max-w-[80%]',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        {/* Role label and Agent indicator */}
        <div className="flex items-center gap-2 mb-1">
          {/* Always show role label for non-user messages, optionally for user */}
          {(showRoleLabel || !isUser) && (
            <span className="text-xs text-muted-foreground font-medium">
              {getRoleLabel()}
            </span>
          )}
          {/* Show agent badge for assistant messages with agent name */}
          {message.agentName && !isUser && (
            <AgentIndicator agentName={message.agentName} />
          )}
        </div>

        {/* Message bubble */}
        <div
          className={cn(
            'rounded-2xl overflow-hidden',
            isCompact ? 'px-3 py-2' : 'px-4 py-3',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-md'
              : isSystem
                ? 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-300 rounded-tl-md border border-gray-200 dark:border-gray-700'
                : isTool
                  ? 'bg-orange-50 text-orange-900 dark:bg-orange-950 dark:text-orange-100 rounded-tl-md border border-orange-200 dark:border-orange-800'
                  : 'bg-muted text-foreground rounded-tl-md'
          )}
        >
          {isCollapsible ? (
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <div className="text-sm break-words">
                {/* Preview content when collapsed */}
                {!isExpanded && (
                  <span className={cn(
                    isTool && extractedJson && 'text-muted-foreground italic'
                  )}>
                    {isTool && extractedJson
                      ? getPreview(message.content, message.role, extractedJson)
                      : renderContent(getPreview(message.content, message.role), message.role)
                    }
                  </span>
                )}
                
                {/* Full content when expanded */}
                <CollapsibleContent>
                  {renderContent(message.content, message.role)}
                </CollapsibleContent>
              </div>
              
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'mt-2 h-6 px-2 text-xs',
                    isUser 
                      ? 'text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10' 
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {isExpanded ? (
                    <>
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronRight className="h-3 w-3 mr-1" />
                      Show more
                    </>
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          ) : (
            <div className="text-sm break-words">
              {renderContent(message.content, message.role)}
            </div>
          )}
        </div>

        {/* Timestamp */}
        {showTimestamp && message.timestamp && (
          <span className="text-xs text-muted-foreground mt-1">
            {formatTime(message.timestamp)}
          </span>
        )}
      </div>
    </article>
  )
}
